import { BrandedQr } from '../ui/brandcode';
import { accentOf, markOf } from './maker';
import { C, Explainer, PhoneFrame, Tap, rise, type ExplainerProps, type SceneDef, type SceneProps } from './engine';
import { fitMono } from './unit';
import { alpha, lift } from '../phone/data';

const T = { aim: 450, lock: 1250, page: 2050, actions: 2400, tap: 3650, chat: 3950, ask: 4150, typing: 4550, answer: 5250, cite: 5650, manual: 5950 };
const ACTIONS = ['Manual for your build', 'Genuine parts', 'Book a service', 'Ask a question'];

function Chevron({ color = C.muted }: { color?: string }) {
  return (
    <svg width="8" height="10" viewBox="0 0 8 10" fill="none">
      <path d="M2 1.5 5.5 5 2 8.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Viewfinder corner bracket. */
function Corner({ r, x, y, on }: { r: number; x: number; y: number; on: boolean }) {
  return (
    <span
      className="xp-abs"
      style={{
        left: 0,
        top: 0,
        width: 18,
        height: 18,
        borderTop: `2.5px solid ${on ? C.lime : '#fff'}`,
        borderLeft: `2.5px solid ${on ? C.lime : '#fff'}`,
        borderTopLeftRadius: 6,
        transform: `translate(${x}px, ${y}px) rotate(${r}deg)`,
        transition: 'transform .75s var(--xe), border-color .15s',
      }}
    />
  );
}

function OwnerScanScene({ t, tone, u }: SceneProps) {
  const BRAND = u.brand.toUpperCase();
  const [doc, page] = splitSource(u.source);
  const at = (ms: number) => t >= ms;
  const dark = tone === 'dark';
  const locked = at(T.lock);
  // viewfinder vertices: wide, then tight on the QR (screen coords; QR spans 59..151 x 108..200)
  const k = at(T.aim) ? 0 : 1;
  const L = 52 - k * 30;
  const R = 158 + k * 30;
  const Tp = 101 - k * 44;
  const B = 207 + k * 44;
  // The page that opens: the dark owner screen in the maker's accent (see src/v2/phone).
  const acc = accentOf(u);
  const accL = lift(acc, 0.52);
  const card = { background: 'rgba(255,255,255,.06)', boxShadow: 'inset 0 0 0 .5px rgba(255,255,255,.1)' };
  return (
    <>
      {/* The machine: panel + plate + tag (what the owner points at) */}
      <div className="xp-abs" style={{ left: 16, top: 18, width: 214, height: 150, borderRadius: 14, background: dark ? '#3B3E47' : '#34373F', boxShadow: dark ? 'inset 0 0 0 1px rgba(255,255,255,.08)' : 'none' }}>
        <div className="xp-abs" style={{ left: 16, top: 18, width: 182, height: 112, borderRadius: 6, background: '#DCDEE4', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.12), 0 2px 0 rgba(0,0,0,.18)', color: '#3A3D48' }}>
          <div className="xp-abs" style={{ left: 12, top: 11, fontSize: 9, fontWeight: 800, letterSpacing: '.12em', whiteSpace: 'nowrap' }}>{BRAND}</div>
          <div className="xp-abs xp-mono" style={{ left: 12, top: 32, fontSize: 7.5, lineHeight: '13px', letterSpacing: '.03em' }}>
            <div>
              <span style={{ opacity: 0.55 }}>MODEL </span>{u.plateModel}
            </div>
            <div>
              <span style={{ opacity: 0.55 }}>SERIAL</span>
            </div>
            <div style={{ fontSize: fitMono(u.serial, 88, 7.5), whiteSpace: 'nowrap' }}>{u.serial}</div>
          </div>
          <div className="xp-abs" style={{ left: 104, top: 10, width: 68, height: 90, background: '#fff', borderRadius: 4, boxShadow: '0 0 0 1px rgba(0,0,0,.1)', padding: '6px 0 0', textAlign: 'center' }}>
            <div style={{ fontSize: 5.5, fontWeight: 800, letterSpacing: '.14em', color: accentOf(u), whiteSpace: 'nowrap' }}>{BRAND}</div>
            <div style={{ width: 46, margin: '4px auto 3px', boxShadow: locked ? `0 0 0 2px ${C.green}` : 'none', transition: 'box-shadow .2s' }}>
              <BrandedQr accent={accentOf(u)} mark={markOf(u)} seed={u.serial} size={46} />
            </div>
            <div className="xp-mono" style={{ fontSize: Math.min(5.5, fitMono(u.serial, 64, 5.5)), color: C.indigo, letterSpacing: 0, whiteSpace: 'nowrap' }}>{u.serial}</div>
          </div>
        </div>
      </div>
      <div className="xp-abs xp-cap" style={{ left: 18, top: 176, fontSize: 9, color: 'var(--xp-muted)' }}>
        No app · No password
      </div>

      {/* Manual page the answer came from */}
      <div className="xp-abs xp-card xp-a" style={{ ...rise(at(T.manual), 0, 10), left: 16, top: 200, width: 214, padding: '11px 13px 12px', zIndex: 3 }}>
        <div className="xp-row" style={{ justifyContent: 'space-between' }}>
          <span className="xp-cap" style={{ fontSize: 7.5, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
            {doc} · {u.model}
          </span>
          <span className="xp-mono" style={{ fontSize: 8, fontWeight: 600, color: C.emerald }}>
            {page.toUpperCase()}
          </span>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, marginTop: 6 }}>{u.section}</div>
        <div style={{ marginTop: 7 }}>
          {[88, 96].map((w, i) => (
            <div key={i} style={{ height: 4, width: `${w}%`, borderRadius: 3, background: '#E9E8F2', marginBottom: 5 }} />
          ))}
          <div style={{ fontSize: 9, lineHeight: 1.35, fontWeight: 500, margin: '1px 0 6px' }}>
            <span style={{ background: at(T.manual + 350) ? 'rgba(143,213,58,.38)' : 'rgba(143,213,58,0)', boxShadow: at(T.manual + 350) ? '0 0 0 2px rgba(143,213,58,.38)' : 'none', borderRadius: 2, transition: 'background .3s, box-shadow .3s' }}>{u.excerpt}</span>
          </div>
          {[92, 70].map((w, i) => (
            <div key={i} style={{ height: 4, width: `${w}%`, borderRadius: 3, background: '#E9E8F2', marginBottom: 5 }} />
          ))}
        </div>
      </div>

      {/* citation → manual */}
      <svg className="xp-abs" style={{ left: 0, top: 0, zIndex: 4 }} width={480} height={360}>
        <path d="M 274 296 C 252 296, 252 262, 232 262" fill="none" stroke={dark ? C.lime : C.emerald} strokeWidth="1.25" strokeDasharray="70" strokeDashoffset={at(T.manual) ? 0 : 70} style={{ transition: 'stroke-dashoffset .45s var(--xe)' }} />
      </svg>

      {/* The owner's phone */}
      <PhoneFrame x={254} y={16} w={212} h={400} tone={tone} style={{ background: 'linear-gradient(150deg, #4A4D55 0%, #1B1C20 22%, #121316 60%, #2C2E34 100%)', boxShadow: '0 30px 60px -24px rgba(8,8,20,.65), inset 0 0 0 1px rgba(255,255,255,.14)' }}>
        {/* camera */}
        <div className="xp-abs" style={{ inset: 0, background: '#2A2C33', overflow: 'hidden' }}>
          <div className="xp-abs" style={{ left: -60, top: 66, width: 330, height: 176, borderRadius: 10, background: '#D5D7DD', transform: at(T.aim) ? 'scale(1)' : 'scale(.9)', transition: 'transform 1.2s var(--xe)' }}>
            <div className="xp-abs" style={{ left: 103, top: 14, width: 124, height: 150, background: '#fff', borderRadius: 6, textAlign: 'center', paddingTop: 10 }}>
              <div style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '.16em', color: accentOf(u), whiteSpace: 'nowrap' }}>{BRAND}</div>
              <div style={{ width: 92, margin: '6px auto 0' }}>
                <BrandedQr accent={accentOf(u)} mark={markOf(u)} seed={u.serial} size={92} />
              </div>
            </div>
          </div>
          <Corner r={0} x={L} y={Tp} on={locked} />
          <Corner r={90} x={R - 18} y={Tp} on={locked} />
          <Corner r={270} x={L} y={B - 18} on={locked} />
          <Corner r={180} x={R - 18} y={B - 18} on={locked} />
          <div className="xp-abs xp-cap" style={{ left: 0, right: 0, top: 40, textAlign: 'center', fontSize: 8, color: 'rgba(255,255,255,.7)' }}>
            Point at the code
          </div>
          <div className="xp-abs xp-a" style={{ ...rise(locked, 60), left: 0, right: 0, top: 262, textAlign: 'center' }}>
            <span className="xp-mono" style={{ display: 'inline-block', fontSize: 9, fontWeight: 600, padding: '5px 10px', borderRadius: 20, background: '#fff', color: C.indigo }}>
              {u.serial}
            </span>
          </div>
        </div>

        {/* unit page */}
        <div className="xp-abs" style={{ inset: 0, color: '#F3F4F6', background: `radial-gradient(130% 40% at 50% -4%, ${alpha(acc, 0.5)} 0%, rgba(0,0,0,0) 62%), linear-gradient(180deg, #121419 0%, #0A0B0E 60%)`, transform: at(T.page) ? 'translateY(0)' : 'translateY(102%)', transition: 'transform .55s var(--xe)' }}>
          <div style={{ height: 98, padding: '36px 14px 0', color: '#fff', position: 'relative' }}>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '.18em', color: accL, whiteSpace: 'nowrap' }}>{BRAND}</div>
            <div style={{ fontSize: u.title.length > 17 ? 12 : 14, fontWeight: 700, letterSpacing: '-.02em', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.title}</div>
            <div style={{ fontSize: 8.5, opacity: 0.55, marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>
              SN {u.serial}
            </div>
          </div>
          <div style={{ padding: '10px 14px 0', visibility: at(T.chat) ? 'hidden' : 'visible' }}>
            <div className="xp-row" style={{ gap: 6, fontSize: 9.5, fontWeight: 500, color: 'rgba(255,255,255,.75)' }}>
              <span style={{ width: 6, height: 6, borderRadius: 6, background: '#5BD98B', boxShadow: '0 0 0 3px rgba(91,217,139,.18)' }} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Warranty active · {u.ownerShort}</span>
            </div>
            <div style={{ marginTop: 10 }}>
              {ACTIONS.map((a, i) => (
                <div key={a} className="xp-row xp-a" style={{ ...rise(at(T.actions + i * 90), 0, 8), justifyContent: 'space-between', height: 36, padding: '0 11px', marginBottom: 6, borderRadius: 11, ...card, fontSize: 10.5, fontWeight: 500, color: '#fff', ...(i === 3 ? { background: acc, boxShadow: 'inset 0 0 0 .5px rgba(255,255,255,.22)', fontWeight: 600 } : null), ...(i === 3 && at(T.tap) ? { filter: 'brightness(1.15)' } : null) }}>
                  {a}
                  <Chevron color={i === 3 ? '#fff' : 'rgba(255,255,255,.35)'} />
                </div>
              ))}
            </div>
            <Tap x={110} y={3 * 42 + 46} on={at(T.tap) && !at(T.chat)} />
          </div>

          {/* chat */}
          <div className="xp-abs" style={{ left: 0, right: 0, top: 98, bottom: 0, padding: '10px 12px', visibility: at(T.chat) ? 'visible' : 'hidden' }}>
            <div className="xp-cap" style={{ fontSize: 7.5, color: 'rgba(255,255,255,.5)' }}>
              Ask about this unit
            </div>
            <div className="xp-a" style={{ ...rise(at(T.ask), 0, 8), marginTop: 9, marginLeft: 34, padding: '8px 10px', borderRadius: '12px 12px 3px 12px', background: acc, color: '#fff', fontSize: 10, lineHeight: 1.4 }}>
              {u.question}
            </div>
            <div className="xp-row" style={{ gap: 3, marginTop: 9, height: at(T.typing) && !at(T.answer) ? 22 : 0, overflow: 'hidden', opacity: at(T.typing) && !at(T.answer) ? 1 : 0 }}>
              {[0, 1, 2].map((i) => (
                <span key={i} style={{ width: 5, height: 5, borderRadius: 5, background: 'rgba(255,255,255,.5)', animation: `xp-blink 1s ${i * 0.15}s infinite` }} />
              ))}
            </div>
            <div className="xp-a" style={{ ...rise(at(T.answer), 0, 8), marginTop: 9, marginRight: 6, padding: '9px 10px', borderRadius: '12px 12px 12px 3px', ...card, color: 'rgba(255,255,255,.9)', fontSize: 10, lineHeight: 1.45 }}>
              {u.answer}
              <div
                className="xp-row xp-mono xp-a"
                style={{ ...rise(at(T.cite), 0, 4), gap: 5, marginTop: 7, paddingTop: 6, borderTop: '.5px solid rgba(255,255,255,.12)', fontSize: 7.5, color: accL, fontWeight: 600, letterSpacing: 0, whiteSpace: 'nowrap' }}
              >
                <svg width="9" height="11" viewBox="0 0 9 11" fill="none">
                  <path d="M1 1h5l2 2v7H1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                </svg>
                Source: {u.source}
              </div>
            </div>
          </div>
        </div>
      </PhoneFrame>
    </>
  );
}

/** "Operator manual p.42" → ["Operator manual", "p.42"]. */
function splitSource(src: string): [string, string] {
  const m = src.match(/^(.*?)[\s,]+(p\.?\s*\d+)$/i);
  return m ? [m[1], m[2].replace(/\s/g, '')] : [src, ''];
}

export const ownerScanDef: SceneDef = {
  name: 'OwnerScan',
  label: (u) => `An owner scans the QR on a ${u.brand} ${u.title}. The page for that exact unit opens with its manual, genuine parts, service booking and questions. A question gets an answer that cites its source: ${u.source}.`,
  W: 480,
  H: 360,
  duration: 9200,
  end: 8600,
  steps: [
    { at: 0, label: 'Scan the plate' },
    { at: T.page, label: 'That exact unit' },
    { at: T.chat, label: 'Ask a question' },
    { at: T.cite, label: 'Answer with a source' },
  ],
  Scene: OwnerScanScene,
};

export const OwnerScanExplainer = (p: ExplainerProps) => <Explainer def={ownerScanDef} {...p} />;
